import { useState, useEffect } from 'react';

const STORAGE_KEY_PREFIX = 'panel-collapse-';

/**
 * Hook to manage panel collapse/expand state with localStorage persistence
 * @param panelId - Unique identifier for the panel (e.g., 'vitals', 'loadout', 'scene')
 * @param defaultExpanded - Default state if no stored value exists (default: true)
 * @returns [isExpanded, toggleExpanded] - Current state and toggle function
 */
export function usePanelCollapse(panelId: string, defaultExpanded = true) {
  const storageKey = `${STORAGE_KEY_PREFIX}${panelId}`;

  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) {
        return JSON.parse(stored);
      }
      return defaultExpanded;
    } catch (error) {
      console.error(`Failed to parse stored collapse state for ${panelId}:`, error);
      return defaultExpanded;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(isExpanded));
    } catch (error) {
      console.error(`Failed to save collapse state for ${panelId}:`, error);
    }
  }, [isExpanded, storageKey, panelId]);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return [isExpanded, toggleExpanded] as const;
}
